import React from 'react';
import { Typography, Box } from '@mui/material';
import BlogPostTemplate from './BlogPostTemplate';
import { havenColors } from '../../theme/theme';

const NoOneCaresPost: React.FC = () => {
  const content = (
    <Box>
      <Typography component="p">
        Let's face it: No one CAREs about residential controls - and that needs to change.
      </Typography>
      
      <Typography component="p">
        The current state of residential HVAC controls, especially when it comes to Indoor Air Quality (IAQ), 
        is flat-out inadequate. While commercial buildings have sophisticated building automation systems 
        that monitor and control everything from temperature to air quality, residential systems are stuck 
        in the stone age.
      </Typography>

      <Typography variant="h2" component="h2">
        The Problem with Residential Controls
      </Typography>
      
      <Typography component="p">
        Walk into any modern commercial building, and you'll find:
      </Typography>
      
      <ul>
        <li>Multi-zone temperature control</li>
        <li>Continuous air quality monitoring</li>
        <li>Demand-controlled ventilation</li>
        <li>Integrated HVAC and lighting systems</li>
        <li>Real-time data analytics and reporting</li>
      </ul>

      <Typography component="p">
        Now walk into the average home. What do you find? A basic thermostat that maybe - if you're lucky - 
        can be programmed for different temperatures at different times of day. That's it.
      </Typography>

      <blockquote>
        <Typography component="p">
          "We spend 90% of our time indoors, yet residential buildings have the most primitive 
          environmental controls. This disconnect is not just inefficient - it's unhealthy."
        </Typography>
      </blockquote>

      <Typography variant="h2" component="h2">
        Why Indoor Air Quality Matters More Than Ever
      </Typography>

      <Typography component="p">
        The COVID-19 pandemic opened everyone's eyes to the importance of indoor air quality. 
        Suddenly, terms like "air changes per hour" and "MERV ratings" became household vocabulary. 
        But the focus was temporary, and now we're back to ignoring the air we breathe at home.
      </Typography>

      <Typography component="p">
        Here's what most homeowners don't realize:
      </Typography>

      <ul>
        <li><strong>Indoor air can be 2-5 times more polluted than outdoor air</strong></li>
        <li><strong>Poor IAQ contributes to asthma, allergies, and fatigue</strong></li>
        <li><strong>Modern homes are more airtight, trapping pollutants inside</strong></li>
        <li><strong>Traditional HVAC systems can't adapt to changing conditions</strong></li>
      </ul>

      <Typography variant="h2" component="h2">
        The HAVEN Solution
      </Typography>

      <Typography component="p">
        This is exactly why we built HAVEN. We recognized that homeowners deserve the same level 
        of environmental control that commercial buildings have enjoyed for decades.
      </Typography>

      <Typography component="p">
        HAVEN brings intelligent air quality management to residential spaces by:
      </Typography>

      <ul>
        <li>Continuously monitoring air quality parameters</li>
        <li>Automatically adjusting ventilation based on real conditions</li>
        <li>Integrating with existing HVAC systems</li>
        <li>Providing actionable insights to homeowners and HVAC professionals</li>
      </ul>

      <Typography variant="h3" component="h3">
        Professional Installation, Professional Results
      </Typography>

      <Typography component="p">
        We work exclusively with HVAC professionals because residential controls are too important 
        to leave to chance. Just like you wouldn't install your own furnace, IAQ systems need proper 
        design, installation, and commissioning.
      </Typography>

      <Typography component="p">
        Our certified HVAC partners understand:
      </Typography>

      <ul>
        <li>Proper system sizing and placement</li>
        <li>Integration with existing equipment</li>
        <li>Local building codes and requirements</li>
        <li>Ongoing maintenance and support</li>
      </ul>

      <Typography variant="h2" component="h2">
        It's Time to Care About Controls
      </Typography>

      <Typography component="p">
        The residential HVAC industry has made incredible advances in efficiency, but we've 
        neglected the control side of the equation. High-efficiency equipment means nothing 
        if it's not properly controlled.
      </Typography>

      <Typography component="p">
        Homeowners are ready for better. They want:
      </Typography>

      <ul>
        <li>Healthier indoor environments</li>
        <li>Lower energy costs</li>
        <li>Peace of mind about their air quality</li>
        <li>Systems that actually work as intended</li>
      </ul>

      <Typography component="p">
        The technology exists. The demand is there. What we need now is an industry that CAREs 
        enough to implement it.
      </Typography>

      <Typography component="p">
        Because at the end of the day, everyone deserves to breathe better at home.
      </Typography>

      <hr style={{ margin: '2rem 0', border: `1px solid ${havenColors.primaryTeal}20` }} />

      <Typography component="p" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
        <strong>About the Author:</strong> Kevin Hart is passionate about bringing professional-grade 
        air quality solutions to residential spaces. He works with HVAC contractors across North America 
        to implement intelligent IAQ systems that actually make a difference.
      </Typography>
    </Box>
  );

  return (
    <BlogPostTemplate
      title="No One CAREs About Residential Controls – An Open Letter"
      author="Kevin Hart"
      date="September 18, 2024"
      readTime="5 min read"
      category="Controls"
      excerpt="Let's face it: No one CAREs about residential controls - and that needs to change. The current state of residential HVAC controls, especially when it comes to Indoor Air Quality (IAQ), is flat-out inadequate."
      content={content}
    />
  );
};

export default NoOneCaresPost; 